import { Badge, Dropdown, IconButton, Icons, Tooltip } from '@commenda/kakashi';
import { cn } from '@commenda/utils';
import { useState } from 'react';

import { useReport } from '../context/ReportContext';
import { ReportComponentHelper } from '../helpers/ReportComponentHelper';
import { SubtitleHelper } from '../helpers/SubtitleHelper';
import { useComponentFacts } from '../hooks/useComponentFacts';
import {
  useInlineOverride,
  type IOverrideMenuGroup,
  type IOverrideMenuOption,
} from '../hooks/useInlineOverride';
import type { IReportItem } from '../types/report';
import { CustomRangePopover } from './CustomRangePopover';
import { GripIcon } from './GripIcon';
import { ReportComponentBody } from './ReportComponentBody';

interface IComponentFrameProps {
  item: IReportItem;
  onOverride: (item: IReportItem) => void;
}

/** Radio-style rows: the check icon is kept (invisible) on unchecked rows so labels align. */
const radioItems = (options: IOverrideMenuOption[]) =>
  options.map((option) => ({
    label: option.label,
    icon: Icons.Check,
    iconClassName: cn(!option.checked && 'invisible'),
    onClick: option.onSelect,
  }));

const radioGroups = (groups: IOverrideMenuGroup[]) =>
  groups.map((group) => ({ items: radioItems(group.options) }));

interface IOverrideChipProps {
  label: string;
  onClear: () => void;
}

const OverrideChip = ({ label, onClear }: IOverrideChipProps) => (
  <Tooltip
    content="This card uses its own period instead of the report's"
    asChild
  >
    <span
      className="inline-flex h-5 shrink-0 items-center gap-xxs rounded-sm bg-utility-blue-50 pl-sm pr-xxs txt-extra-small-medium text-text-info"
      data-export-ignore
      data-no-drag
    >
      <span className="max-w-[160px] truncate">{label}</span>
      <button
        type="button"
        aria-label="Use report period"
        className="flex size-4 items-center justify-center rounded-xs text-utility-blue-400 transition-colors hover:bg-black/5 hover:text-text-info focus:outline-none focus-visible:ring-2"
        onClick={onClear}
      >
        <Icons.X className="size-3" />
      </button>
    </span>
  </Tooltip>
);

/** Card shell around a report component: drag handle, period subtitle and actions menu. */
export const ComponentFrame = ({ item, onOverride }: IComponentFrameProps) => {
  const { inlineOverride } = useReport();
  const definition = ReportComponentHelper.definition(item.componentId);
  const facts = useComponentFacts(item);
  const { resolved, hasOverride } = facts;
  const isTile = definition.kind === 'tile';
  const subtitle = SubtitleHelper.format({ definition, facts });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const inline = useInlineOverride(item, definition.title);

  const menu = inlineOverride
    ? {
        groups: [
          {
            items: [
              {
                label: 'Period',
                icon: Icons.Calendar,
                subMenu: { groups: radioGroups(inline.periodGroups) },
              },
              {
                label: 'Compare to',
                icon: Icons.SwitchHorizontal01,
                subMenu: {
                  groups: [{ items: radioItems(inline.compareOptions) }],
                },
              },
            ],
          },
        ],
      }
    : {
        groups: [
          {
            items: [
              {
                label: hasOverride ? 'Edit period override' : 'Override period',
                icon: Icons.Calendar,
                onClick: () => onOverride(item),
              },
            ],
          },
        ],
      };

  const header = (
    <div className="relative flex items-start justify-between gap-md border-b border-border-secondary px-lg py-md">
      <Tooltip content="Drag to move" asChild>
        <span
          className="report-drag-handle absolute left-0 top-md flex h-5 w-lg cursor-grab items-center justify-center text-text-quaternary opacity-0 transition-opacity group-hover/frame:opacity-100 hover:text-text-tertiary active:cursor-grabbing"
          data-export-ignore
        >
          <GripIcon />
        </span>
      </Tooltip>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-sm">
          <p className="truncate font-mulish text-sm font-semibold leading-5 tracking-[-2%] text-text-primary">
            {definition.title}
          </p>
          {hasOverride &&
            (inlineOverride ? (
              <OverrideChip
                label={resolved.label}
                onClear={inline.clearOverride}
              />
            ) : (
              <Tooltip content="This card uses its own period instead of the report's">
                <span className="flex">
                  <Badge variant="blue" size="sm">
                    Override
                  </Badge>
                </span>
              </Tooltip>
            ))}
          {resolved.clamped && (
            <Tooltip content="Requested period extends past the last closed month; open months are excluded.">
              <span className="flex" data-export-ignore>
                <Icons.AlertTriangle className="size-3.5 text-fg-warning" />
              </span>
            </Tooltip>
          )}
        </div>
        <Tooltip content={definition.title} supportingText={subtitle} asChild>
          <p className="block max-w-full truncate text-left txt-extra-small-regular text-text-tertiary">
            {subtitle}
          </p>
        </Tooltip>
      </div>
      <div
        className={cn(
          'flex shrink-0 items-center gap-xs opacity-0 transition-opacity group-hover/frame:opacity-100 focus-within:opacity-100',
          isMenuOpen && 'opacity-100'
        )}
        data-export-ignore
        data-no-drag
      >
        <Dropdown
          onOpenChange={(open) => {
            setIsMenuOpen(open);
            inline.onMenuOpenChange(open);
          }}
          menu={menu}
        >
          <IconButton
            size="sm"
            aria-label="Component options"
            icon={
              <Icons.DotsVertical
                className={cn('size-4', hasOverride && 'text-fg-brand')}
              />
            }
          />
        </Dropdown>
      </div>
    </div>
  );

  return (
    <div
      data-report-item={item.instanceId}
      className="group/frame flex h-full flex-col overflow-hidden rounded-lg border border-border-secondary bg-bg-primary shadow-xs"
    >
      {inlineOverride ? (
        <CustomRangePopover
          open={inline.customRange.open}
          onOpenChange={inline.customRange.setOpen}
          idPrefix={`${item.instanceId}-range`}
          initialFrom={inline.customRange.initialFrom}
          initialTo={inline.customRange.initialTo}
          months={inline.customRange.months}
          booksClosedThrough={inline.customRange.booksClosedThrough}
          onApply={inline.customRange.apply}
        >
          {header}
        </CustomRangePopover>
      ) : (
        header
      )}
      <div
        className={cn('min-h-0 flex-1 px-lg py-xl', !isTile && 'overflow-auto')}
      >
        <ReportComponentBody componentId={item.componentId} facts={facts} />
      </div>
    </div>
  );
};
